import type { Vec2, OBB, Segment } from './types';
import { obbCorners } from './polygon';

function project(corners: readonly Vec2[], axis: Vec2): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const c of corners) {
    const dot = c[0] * axis[0] + c[1] * axis[1];
    if (dot < min) min = dot;
    if (dot > max) max = dot;
  }
  return { min, max };
}

function intervalsOverlapStrict(
  a: { min: number; max: number },
  b: { min: number; max: number },
  tolerance: number
): boolean {
  return a.max - tolerance > b.min && b.max - tolerance > a.min;
}

function intervalsOverlapInclusive(
  a: { min: number; max: number },
  b: { min: number; max: number }
): boolean {
  return a.max >= b.min && b.max >= a.min;
}

export function obbOverlap(a: OBB, b: OBB, tolerance: number): boolean {
  const cornersA = obbCorners(a);
  const cornersB = obbCorners(b);

  const axes: Vec2[] = [a.axisU, [-a.axisU[1], a.axisU[0]], b.axisU, [-b.axisU[1], b.axisU[0]]];

  for (const axis of axes) {
    const projA = project(cornersA, axis);
    const projB = project(cornersB, axis);
    if (!intervalsOverlapStrict(projA, projB, tolerance)) {
      return false;
    }
  }

  return true;
}

export function obbSegmentOverlap(o: OBB, s: Segment): boolean {
  const corners = obbCorners(o);
  const segmentCorners: readonly Vec2[] = [s.a, s.b];

  const segDir: Vec2 = [s.b[0] - s.a[0], s.b[1] - s.a[1]];
  const segLen = Math.sqrt(segDir[0] * segDir[0] + segDir[1] * segDir[1]);
  const segNorm: Vec2 = segLen > 1e-10 ? [-segDir[1] / segLen, segDir[0] / segLen] : [0, 1];

  const axes: Vec2[] = [o.axisU, [-o.axisU[1], o.axisU[0]], segNorm];

  for (const axis of axes) {
    const projOBB = project(corners, axis);
    const projSeg = project(segmentCorners, axis);
    if (!intervalsOverlapInclusive(projOBB, projSeg)) {
      return false;
    }
  }

  return true;
}

/** A box's corners and its spans along its own axes, which every segment test against it shares. */
export interface BoxSpans {
  readonly corners: readonly Vec2[];
  readonly axisV: Vec2;
  readonly spanU: { min: number; max: number };
  readonly spanV: { min: number; max: number };
}

export function boxSpans(o: OBB): BoxSpans {
  const corners = obbCorners(o);
  const axisV: Vec2 = [-o.axisU[1], o.axisU[0]];
  return { corners, axisV, spanU: project(corners, o.axisU), spanV: project(corners, axisV) };
}

/** obbSegmentOverlap's answer for the segment from `a` to `b`, with the box's share of it done once. */
export function spansOverlapSegment(o: OBB, box: BoxSpans, a: Vec2, b: Vec2): boolean {
  const ends: readonly Vec2[] = [a, b];
  if (!intervalsOverlapInclusive(box.spanU, project(ends, o.axisU))) return false;
  if (!intervalsOverlapInclusive(box.spanV, project(ends, box.axisV))) return false;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.sqrt(dx * dx + dy * dy);
  const normal: Vec2 = length > 1e-10 ? [-dy / length, dx / length] : [0, 1];
  return intervalsOverlapInclusive(project(box.corners, normal), project(ends, normal));
}

function pointInConvexOBB(point: Vec2, obb: OBB): boolean {
  const dx = point[0] - obb.center[0];
  const dy = point[1] - obb.center[1];

  const projU = dx * obb.axisU[0] + dy * obb.axisU[1];
  const projV = dx * -obb.axisU[1] + dy * obb.axisU[0];

  return Math.abs(projU) <= obb.halfU && Math.abs(projV) <= obb.halfV;
}

export function obbPolygonOverlap(
  obb: OBB,
  polygon: readonly Vec2[],
  _tolerance: number = 0
): boolean {
  if (polygon.length < 3) return false;

  const obbCornersList = obbCorners(obb);

  for (const corner of obbCornersList) {
    if (pointInPolygonRayCast(corner, polygon)) {
      return true;
    }
  }

  for (const polyVertex of polygon) {
    if (pointInConvexOBB(polyVertex, obb)) {
      return true;
    }
  }

  const box = boxSpans(obb);
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    if (spansOverlapSegment(obb, box, polygon[i]!, polygon[j]!)) {
      return true;
    }
  }

  return false;
}

function pointInPolygonRayCast(point: Vec2, polygon: readonly Vec2[]): boolean {
  let inside = false;
  const x = point[0];
  const y = point[1];
  const n = polygon.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i]![0];
    const yi = polygon[i]![1];
    const xj = polygon[j]![0];
    const yj = polygon[j]![1];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}
